import { UserGroupPermissionId } from './UserGroupPermissionId';
export const AUDIO_ORDERING_FEATURE_USER_GROUP_PERMISSIONS_WITH_RESTRICTED_PERMISSIONS = [
    UserGroupPermissionId.ProcessAudioOrders,
];
export const REALTIME_ORDERING_FEATURE_USER_GROUP_PERMISSIONS_WITH_RESTRICTED_PERMISSIONS = [
    UserGroupPermissionId.ProcessRealTimeOrders,
];
export const TRANSCRIPT_ORDERING_FEATURE_USER_GROUP_PERMISSIONS_WITH_RESTRICTED_PERMISSIONS = [
    UserGroupPermissionId.ProcessTranscriptOrders,
];
export const AUDIO_PLAYBACK_FEATURE_USER_GROUP_PERMISSIONS_WITH_RESTRICTED_PERMISSIONS = [
    UserGroupPermissionId.PlaybackRecordings,
    UserGroupPermissionId.ReadLogSheets,
];
export const AUDIO_PLAYBACK_STT_FEATURE_USER_GROUP_PERMISSIONS_WITH_RESTRICTED_PERMISSIONS = [
    UserGroupPermissionId.ReadStt,
    UserGroupPermissionId.RequestStt,
    UserGroupPermissionId.CaseInformation,
];
export const ALL_USER_GROUP_PERMISSIONS_WITH_RESTRICTED_PERMISSIONS = [
    ...AUDIO_ORDERING_FEATURE_USER_GROUP_PERMISSIONS_WITH_RESTRICTED_PERMISSIONS,
    ...REALTIME_ORDERING_FEATURE_USER_GROUP_PERMISSIONS_WITH_RESTRICTED_PERMISSIONS,
    ...TRANSCRIPT_ORDERING_FEATURE_USER_GROUP_PERMISSIONS_WITH_RESTRICTED_PERMISSIONS,
    ...AUDIO_PLAYBACK_FEATURE_USER_GROUP_PERMISSIONS_WITH_RESTRICTED_PERMISSIONS,
    ...AUDIO_PLAYBACK_STT_FEATURE_USER_GROUP_PERMISSIONS_WITH_RESTRICTED_PERMISSIONS,
];
/**
 * Frontend-only grouping for permissions
 */
export var UserPermissionCategory;
(function (UserPermissionCategory) {
    UserPermissionCategory["Admin"] = "Admin";
    UserPermissionCategory["GeneralOrdering"] = "General Ordering";
    UserPermissionCategory["AudioOrders"] = "Audio Orders";
    UserPermissionCategory["RealTimeOrders"] = "Real-Time Orders";
    UserPermissionCategory["TranscriptOrders"] = "Transcript Orders";
    UserPermissionCategory["Recordings"] = "Recordings";
    UserPermissionCategory["RecordingVault"] = "Recording Vault";
    UserPermissionCategory["Sealing"] = "Sealing";
    UserPermissionCategory["RealTimeRecorders"] = "Real-Time Recorders";
    UserPermissionCategory["DigitalJustice"] = "Real-Time Hybrid Hearings";
    UserPermissionCategory["CasesAndHearings"] = "Cases and Hearings";
})(UserPermissionCategory || (UserPermissionCategory = {}));
/**
 * Maps frontend-only descriptions, names and categories to permissions
 */
export const PERMISSIONS = [
    {
        permissionId: UserGroupPermissionId.AdministerBillingGroups,
        name: 'Billing Groups',
        description: 'Create and edit billing groups, including group members.',
        category: UserPermissionCategory.Admin,
    },
    {
        permissionId: UserGroupPermissionId.ConfigureCourtSystem,
        name: 'Configure',
        description: 'Adjust settings for this court system.',
        category: UserPermissionCategory.Admin,
    },
    {
        permissionId: UserGroupPermissionId.AdministerHolidays,
        name: 'Holidays',
        description: 'Add, view or delete holidays in this court system.',
        category: UserPermissionCategory.Admin,
    },
    {
        permissionId: UserGroupPermissionId.AdministerLocations,
        name: 'Locations',
        description: 'Add, view or edit the locations and recorders in this court system.',
        category: UserPermissionCategory.Admin,
    },
    {
        permissionId: UserGroupPermissionId.AdministerAgentConfigAndFileUpload,
        name: 'Legacy Agents',
        description: 'Manage legacy agents, which upload audio and log sheets.',
        category: UserPermissionCategory.RecordingVault,
    },
    {
        permissionId: UserGroupPermissionId.ReadRecordingVault,
        name: 'Explore Recording Vault',
        description: 'Browse and search recording vault repositories.',
        category: UserPermissionCategory.RecordingVault,
    },
    {
        permissionId: UserGroupPermissionId.WriteRecordingVault,
        name: 'Manage Recording Vault',
        description: 'Download agents, prioritize scans and uploads, create repositories.',
        category: UserPermissionCategory.RecordingVault,
    },
    {
        permissionId: UserGroupPermissionId.AdministerUserGroups,
        name: 'User Groups',
        description: 'Create and edit groups, including group members.',
        category: UserPermissionCategory.Admin,
    },
    {
        permissionId: UserGroupPermissionId.RemoveTrms,
        name: 'Remove Files',
        description: 'Permanently remove files from this court system.',
        category: UserPermissionCategory.Admin,
    },
    {
        permissionId: UserGroupPermissionId.AccessReports,
        name: 'Reports',
        description: 'View and download reports.',
        category: UserPermissionCategory.Admin,
    },
    {
        permissionId: UserGroupPermissionId.AccessAuditLogs,
        name: 'Auditing',
        description: 'View audit logs.',
        category: UserPermissionCategory.Admin,
    },
    {
        permissionId: UserGroupPermissionId.PlaceOrdersInUnpublishedCourts,
        name: 'Place Orders in Unpublished Courts',
        description: 'Place orders in unpublished courts, and published courts with public ordering disabled. This is only possible if the court has enabled ordering.',
        category: UserPermissionCategory.GeneralOrdering,
    },
    {
        permissionId: UserGroupPermissionId.ProcessAudioOrders,
        name: 'Process Audio Orders',
        description: 'Approve & process audio orders.',
        category: UserPermissionCategory.AudioOrders,
    },
    {
        permissionId: UserGroupPermissionId.ProcessRealTimeOrders,
        name: 'Process Real-Time Orders',
        description: 'View & process real-time orders.',
        category: UserPermissionCategory.RealTimeOrders,
    },
    {
        permissionId: UserGroupPermissionId.ProcessTranscriptOrders,
        name: 'Process Transcript Orders',
        description: 'Approve and process transcript orders.',
        category: UserPermissionCategory.TranscriptOrders,
    },
    {
        permissionId: UserGroupPermissionId.DistributeTranscripts,
        name: 'Share Transcript Orders',
        description: 'Share a transcript order while ordering. Once the order is complete all users will have access to the transcript order.',
        category: UserPermissionCategory.TranscriptOrders,
    },
    {
        permissionId: UserGroupPermissionId.TranscribeRecordings,
        name: 'Transcriber',
        description: 'Users can be assigned transcript jobs to complete.',
        category: UserPermissionCategory.TranscriptOrders,
    },
    {
        permissionId: UserGroupPermissionId.TranscriberReadStt,
        name: 'View Speech-to-Text',
        description: 'Transcribers can view text on assigned jobs, where it already exists.',
        category: UserPermissionCategory.TranscriptOrders,
    },
    {
        permissionId: UserGroupPermissionId.TranscriberReadLogSheets,
        name: 'View Log Sheets',
        description: 'Transcribers can view log sheets associated with assigned jobs.',
        category: UserPermissionCategory.TranscriptOrders,
    },
    {
        permissionId: UserGroupPermissionId.PlaybackRecordings,
        name: 'Browse Recordings',
        description: 'Browse and play all recordings.',
        category: UserPermissionCategory.Recordings,
    },
    {
        permissionId: UserGroupPermissionId.ReadStt,
        name: 'View Text',
        description: 'View text on recordings, where it already exists.',
        category: UserPermissionCategory.Recordings,
    },
    {
        permissionId: UserGroupPermissionId.RequestStt,
        name: 'Request Text',
        description: 'Request text on recordings.',
        category: UserPermissionCategory.Recordings,
    },
    {
        permissionId: UserGroupPermissionId.CaseInformation,
        name: 'Manage Case Information',
        description: 'Create, edit and delete case information on recordings.',
        category: UserPermissionCategory.Recordings,
    },
    {
        permissionId: UserGroupPermissionId.ReadLogSheets,
        name: 'View Log Sheets',
        description: 'View log sheets associated with recordings.',
        category: UserPermissionCategory.Recordings,
    },
    {
        permissionId: UserGroupPermissionId.ShareRecordings,
        name: 'Share Recordings',
        description: 'Share recordings with other users.',
        category: UserPermissionCategory.Recordings,
    },
    {
        permissionId: UserGroupPermissionId.DownloadBundles,
        name: 'Download Recordings',
        description: 'Generate and download bundles containing recordings.',
        category: UserPermissionCategory.Recordings,
    },
    {
        permissionId: UserGroupPermissionId.SealContent,
        name: 'Seal Content',
        description: 'Seal and unseal audio, video and log sheets.',
        category: UserPermissionCategory.Sealing,
    },
    {
        permissionId: UserGroupPermissionId.AccessSealedContent,
        name: 'View Sealed Content',
        description: 'View sealed content on recordings, audio and transcript orders.',
        category: UserPermissionCategory.Sealing,
    },
    {
        permissionId: UserGroupPermissionId.ViewRealTimeRecorders,
        name: 'View Real-Time Recorders',
        description: 'View and monitor running Real-Time Recorders.',
        category: UserPermissionCategory.RealTimeRecorders,
    },
    {
        permissionId: UserGroupPermissionId.ManageRealtimeRecorders,
        name: 'Manage Real-Time Recorders',
        description: 'Configure and download Real-Time Recorders.',
        category: UserPermissionCategory.RealTimeRecorders,
    },
    {
        permissionId: UserGroupPermissionId.AdvancedRealtimeRecorderConfiguration,
        name: 'Advanced Real-Time Recorder Configuration',
        description: 'Configure advanced Real-Time Recorder settings (AEC).',
        category: UserPermissionCategory.RealTimeRecorders,
    },
    {
        permissionId: UserGroupPermissionId.ModerateProceedings,
        name: 'Moderate Proceedings',
        description: 'Moderate Real-Time Hybrid proceedings.',
        category: UserPermissionCategory.DigitalJustice,
    },
    {
        permissionId: UserGroupPermissionId.ManageWhenCourtroomIsOnTheRecord,
        name: 'Start and Stop Live Sessions',
        description: 'Control when the court is in or out of session in real time.',
        category: UserPermissionCategory.RealTimeRecorders,
    },
    {
        permissionId: UserGroupPermissionId.AccessOffTheRecordContent,
        name: 'Access Out of Session Content',
        description: 'Access out of session content for playback.',
        category: UserPermissionCategory.RealTimeRecorders,
    },
    {
        permissionId: UserGroupPermissionId.ManagePastSessions,
        name: 'Manage Past Sessions',
        description: 'Add, update and delete sessions on the playback page. Requires “Access Out of Session Content” permission.',
        category: UserPermissionCategory.RealTimeRecorders,
    },
    {
        permissionId: UserGroupPermissionId.PlaybackHearings,
        name: 'Browse Cases and Hearings',
        description: 'Browse and playback cases and hearings.',
        category: UserPermissionCategory.CasesAndHearings,
    },
];
export function getPermissionDescription(id) {
    var _a;
    return (_a = PERMISSIONS.find(p => p.permissionId === id)) === null || _a === void 0 ? void 0 : _a.description;
}
export function getPermissionName(id) {
    var _a;
    return (_a = PERMISSIONS.find(p => p.permissionId === id)) === null || _a === void 0 ? void 0 : _a.name;
}
